import type { CannedResponseSummary } from '@livechat/shared';
import { type KeyboardEvent, useEffect, useRef } from 'react';

import { isCannedSelectionKey } from '../canned-responses.js';

interface CannedResponseMenuProps {
	activeIndex: number;
	onActiveIndexChange: (index: number) => void;
	onClose: () => void;
	onQueryChange: (query: string) => void;
	onSelect: (response: CannedResponseSummary) => void;
	query: string;
	responses: CannedResponseSummary[];
	searchOnOpen: boolean;
}

export function CannedResponseMenu({
	activeIndex,
	onActiveIndexChange,
	onClose,
	onQueryChange,
	onSelect,
	query,
	responses,
	searchOnOpen,
}: CannedResponseMenuProps) {
	const searchRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (searchOnOpen) searchRef.current?.focus();
	}, [searchOnOpen]);

	function keyDown(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			event.preventDefault();
			onClose();
		} else if (event.key === 'ArrowDown') {
			event.preventDefault();
			onActiveIndexChange(responses.length ? (activeIndex + 1) % responses.length : 0);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			onActiveIndexChange(
				responses.length ? (activeIndex - 1 + responses.length) % responses.length : 0,
			);
		} else if (isCannedSelectionKey(event.key) && responses[activeIndex]) {
			event.preventDefault();
			onSelect(responses[activeIndex]);
		}
	}

	return (
		<div className="canned-menu" role="dialog" aria-label="Canned responses">
			<div className="canned-menu-search">
				<input
					aria-label="Search canned responses"
					onChange={(event) => onQueryChange(event.currentTarget.value)}
					onKeyDown={keyDown}
					placeholder="Search responses…"
					ref={searchRef}
					value={query}
				/>
				<span>↑↓ navigate · Tab or Enter insert · Esc close</span>
			</div>
			<div className="canned-menu-results" role="listbox">
				{responses.map((response, index) => (
					<button
						aria-selected={index === activeIndex}
						className={index === activeIndex ? 'active' : ''}
						key={response.id}
						onClick={() => onSelect(response)}
						onMouseEnter={() => onActiveIndexChange(index)}
						role="option"
						type="button"
					>
						<span className={`scope-badge ${response.scope}`}>{response.scope}</span>
						<span className="canned-menu-copy">
							<strong>{response.name}</strong>
							<small>{response.body}</small>
						</span>
						{response.trigger && <code>/{response.trigger}</code>}
					</button>
				))}
				{responses.length === 0 && (
					<div className="canned-menu-empty">No active responses match your search.</div>
				)}
			</div>
		</div>
	);
}
